import React, { useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import { useParams } from "react-router-dom";
import SmallInput from "../Components/SmallInput";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPlus } from "@fortawesome/free-solid-svg-icons";
import Input from "../Components/Input";
import useApi from "../Api/Api";
import SalesLayout from "./SalesLayout";
import EventLayout from "./EventLayout";
import Button from "../Components/Button";
import useLocalState from "../hooks/useLocalState";
import useEventSales from "../hooks/useEventSales";
import cn from "classnames";
import CleaveInput from "../Components/CleaveInput";
import storageManager from "../Utility/StorageManager";
import OnlineWrapper from "./OnlineWrapper";
import OfflineWrapper from "./OfflineWrapper";
import { DateTime } from "luxon";

const EventSales = () => {
  const api = useApi();
  const params = useParams();
  const [errorMessage, setErrorMessage] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [dataCached, setDataCached] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [salesToAlter, setSalesToAlter] = useState([]);
  const [searchField, setSearchField] = useState("");
  const [showAdd3, setShowAdd3] = useState(false);
  const [showAdd4, setShowAdd4] = useState(false);
  const [showOutBorder, setShowOutBorder] = useState(false);

  const [showPrevOuts, setShowPrevOuts] = useLocalState(`showPrevOuts`, {});

  const { eventSales, setEventSales, getEventSales } = useEventSales(
    params.eventId,
    storageManager.getItem(`previous_event_id`)
  );

  const [event, setEvent] = useState({
    date: "",
    id: params.eventId,
  });

  const categoryOptions = categories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const [alterSalesObj, setAlterSalesObj] = useLocalState(`event/${params.eventId}/sales`, {});

  useEffect(() => {
    storageManager.getItem(`event/${params.eventId}/sales`) &&
      setAlterSalesObj(JSON.parse(storageManager.getItem(`event/${params.eventId}/sales`)));
  }, [params.eventId]); // eslint-disable-line

  let mergedSales = [...eventSales];

  Object.values(alterSalesObj).forEach((each) => {
    if (!each.discarded_at && each.new) {
      if (!mergedSales.find((sale) => sale.product_id === each.product_id)) {
        mergedSales.push(each);
      }
    } else if (each.discarded_at) {
      mergedSales = mergedSales.filter((sale) => sale.product_id !== each.product_id);
    }
  });

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setSalesToAlter(
        mergedSales
          .filter((each) => each.product_name.toLowerCase().includes(searchField))
          .filter((each) => each.category_id.includes(categoryFilter))
      );
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [alterSalesObj, eventSales, searchField, categoryFilter]); // eslint-disable-line

  salesToAlter
    .sort((a, b) => {
      if (a.product && b.product) {
        return DateTime.fromISO(a.product.created_at) > DateTime.fromISO(b.product.created_at) ? 1 : -1;
      } else {
        return DateTime.fromISO(a.created_at) > DateTime.fromISO(b.created_at) ? 1 : -1;
      }
    })
    .sort((a, b) => (a.size?.index < b.size?.index ? 1 : -1))
    .sort((a, b) => b.product_name?.localeCompare(a.product_name))
    .sort((a, b) => (a.category?.index > b.category?.index ? 1 : -1));

  for (let i = 0; i < salesToAlter.length; i++) {
    const saleToCalculateFrom = alterSalesObj[salesToAlter[i].product_id] || salesToAlter[i];
    saleToCalculateFrom.discount_price = saleToCalculateFrom.discount_price || 0;
    saleToCalculateFrom.price = saleToCalculateFrom.price || 0;
    salesToAlter[i].total =
      (parseInt(saleToCalculateFrom.count_in) +
        parseInt(saleToCalculateFrom.add) +
        parseInt(saleToCalculateFrom.add_2) +
        parseInt(saleToCalculateFrom.add_3) +
        parseInt(saleToCalculateFrom.add_4) -
        saleToCalculateFrom.count_out -
        saleToCalculateFrom.damage_quantity -
        saleToCalculateFrom.discount_quantity -
        saleToCalculateFrom.band_comp_quantity -
        saleToCalculateFrom.ctm_comp -
        saleToCalculateFrom.management_comp) *
      parseFloat(saleToCalculateFrom.price.toString().replace(/,/g, ""));
    salesToAlter[i].discount_total =
      saleToCalculateFrom.discount_quantity *
      parseFloat(saleToCalculateFrom.discount_price.toString().replace(/,/g, ""));
    salesToAlter[i].add3 = saleToCalculateFrom.add_3;
    salesToAlter[i].add4 = saleToCalculateFrom.add_4;
  }

  const grossAll = salesToAlter.reduce((a, b) => {
    return a + b.total;
  }, 0);

  const discountAll = salesToAlter.reduce((a, b) => {
    return a + b.discount_total;
  }, 0);

  const totalAdd3s = salesToAlter.reduce((a, b) => {
    return a + b.add3;
  }, 0);

  const totalAdd4s = salesToAlter.reduce((a, b) => {
    return a + b.add4;
  }, 0);

  const reset = () => {
    setSearchField("");
    setCategoryFilter("");
  };

  const handleSearchChange = (e) => {
    setSearchField(e.target.value.toLowerCase());
  };

  const handleFilterCategory = (option) => {
    setCategoryFilter(option.value);
  };

  const handleShowAdd3 = () => {
    setShowAdd3(true);
  };

  useEffect(() => {
    totalAdd3s > 0 && setShowAdd3(true);
  }, [totalAdd3s]);

  const handleShowAdd4 = () => {
    setShowAdd4(true);
  };

  useEffect(() => {
    totalAdd4s > 0 && setShowAdd4(true);
  }, [totalAdd4s]);

  const selectCell = (e) => {
    e.target.select();
    e.target.id === "count_out" ? setShowOutBorder(true) : setShowOutBorder(false);
  };

  const setPrevCountOutEqCurrentCountIn = () => {
    setShowButtons(true);
    eventSales.forEach((sale) => {
      let currentEventState = JSON.parse(storageManager.getItem(`event/${params.eventId}/sales`) ?? "{}");
      currentEventState[sale.product_id] = sale;
      currentEventState[sale.product_id].count_in = sale.previous_count_out;
      currentEventState[sale.product_id].sale_id = sale.id;
      storageManager.setItem(`event/${params.eventId}/sales`, JSON.stringify(currentEventState));
    });
    setAlterSalesObj(JSON.parse(storageManager.getItem(`event/${params.eventId}/sales`)));
  };

  const handleInputChange = ({ target: { name, value } }, sale, offlineState) => {
    console.log("EventSales", "handleInputChange", name, value);
    sale.total =
      (parseInt(sale.count_in) +
        parseInt(sale.add) +
        parseInt(sale.add_2) +
        parseInt(sale.add_3) +
        parseInt(sale.add_4) -
        sale.count_out -
        sale.damage_quantity -
        sale.discount_quantity -
        sale.band_comp_quantity -
        sale.ctm_comp -
        sale.management_comp) *
      sale.price;
    setShowButtons(true);
    setDataCached(offlineState);
    let currentEventState = JSON.parse(storageManager.getItem(`event/${params.eventId}/sales`) ?? "{}");
    currentEventState[sale.product_id] = sale;
    currentEventState[sale.product_id][name] = value;
    currentEventState[sale.product_id].total =
      (parseInt(sale.count_in) +
        parseInt(sale.add) +
        parseInt(sale.add_2) +
        parseInt(sale.add_3) +
        parseInt(sale.add_4) -
        sale.count_out -
        sale.damage_quantity -
        sale.discount_quantity -
        sale.band_comp_quantity -
        sale.ctm_comp -
        sale.management_comp) *
      sale.price;
    if (!sale.new) {
      currentEventState[sale.product_id].sale_id = sale.id;
    }
    storageManager.setItem(`event/${params.eventId}/sales`, JSON.stringify(currentEventState));
    setAlterSalesObj(JSON.parse(storageManager.getItem(`event/${params.eventId}/sales`)));
  };

  useEffect(() => {
    storageManager.setItem(`event/${params.eventId}/originalSales`, JSON.stringify(eventSales));
  }, [eventSales, params.eventId]);

  const afterSubmit = () => {
    setShowButtons(false);
    setAlterSalesObj({});
    getEventSales();
  };

  const revert = () => {
    let message = "Are you sure you want to revert? All unsaved changes will be lost.";
    const check = window.confirm(message);
    if (check) {
      setShowButtons(false);
      setEventSales(JSON.parse(storageManager.getItem(`event/${params.eventId}/originalSales`)));
      setAlterSalesObj({});
    }
  };

  const handleUpdateSales = (e) => {
    if (grossAll.toString() === "NaN") {
      window.alert(
        "Gross Sales is not a valid number, make sure all cells are filled in with a number. Do not leave any cells empty, instead enter a 0"
      );
    } else {
      e.preventDefault();
      Object.values(alterSalesObj).forEach((one) => {
        one.sale_id ? (one.id = one.sale_id) : delete one.id;
      });
      api
        .put(`/events/${event.id}`, {
          event: {
            sales_attributes: Object.values(alterSalesObj).map((each) => each),
          },
        })
        .then(() => {
          NotificationManager.success("Sales successfully updated", "Success", 2000);
          afterSubmit();
          setShowPrevOuts(false);
        })
        .catch((error) => {
          setErrorMessage(error.response.data);
        })
        .finally(() => {});
    }
  };

  const handleStoreSales = () => {
    NotificationManager.warning(
      "You are currently operating offline. Your changes have been cached for now, and you will be able to sync once you are online again.",
      "Warning",
      10000
    );
    setShowButtons(false);
    setShowPrevOuts(false);
  };

  useEffect(() => {
    api.get(`/categories`).then((r) => {
      setCategories(r.data);
    });
  }, [api]);

  useEffect(() => {
    api.get(`/events/${params.eventId}`).then((r) => {
      setEvent(r.data);
    });
  }, [api, setEvent, params.eventId]);

  const columns = [
    { label: "PREV SHOW COUNT OUT", value: "previous_count_out" },
    { label: "IN", value: "count_in" },
    { label: "ADD 1", value: "add" },
    { label: "ADD 2", value: "add_2" },
    { label: "ADD 3", value: "add_3" },
    { label: "ADD 4", value: "add_4" },
    { label: "OUT", value: "count_out" },
    { label: "DISCOUNT", value: "discount_quantity" },
    { label: "ARTIST COMP", value: "band_comp_quantity" },
    { label: "TM COMP", value: "ctm_comp" },
    { label: "SALE PRICE", value: "price" },
    { label: "DAMAGE", value: "damage_quantity" },
    { label: "DISCOUNT PRICE", value: "discount_price" },
  ];

  let count = 0;

  for (let i = 1; i < salesToAlter.length; i++) {
    if (salesToAlter[i - 1].product_name !== salesToAlter[i].product_name) {
      count++;
    }
    salesToAlter[i].productNumber = count;
  }

  const renderTable = (salesToAlter, offlineState) => {
    return (
      <div className="flex w-full">
        <table className="w-1/4">
          <thead className="text-xs font-light">
            <tr className="text-left border-b-2 h-11 text-center sticky top-0 bg-white z-10">
              <td className="w-1/5 text-left pl-6">PRODUCT</td>
            </tr>
          </thead>
          <tbody>
            {salesToAlter.map((saleToRender) => {
              const productToRender = alterSalesObj[saleToRender.product_id] || saleToRender;
              return (
                <tr
                  key={productToRender.id}
                  className={cn("border-b text-sm h-11 text-center", {
                    "bg-yellow-50": alterSalesObj[saleToRender.product_id],
                    "bg-gray-100": productToRender.productNumber % 2 === 1,
                  })}
                >
                  {productToRender.product_size ? (
                    <td className="text-left text-sm pl-6">
                      {productToRender.product_name} - {productToRender.product_size}
                    </td>
                  ) : (
                    <td className="text-left text-sm pl-6">{productToRender.product_name}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {columns.map((attribute) => {
          return (
            <table
              className={cn({
                "w-thin": attribute.value === "price" || attribute.value === "discount_price",
                "w-thinner":
                  attribute.value !== "price" &&
                  attribute.value !== "discount_price" &&
                  attribute.value !== "add_3" &&
                  attribute.value !== "add_4" &&
                  attribute.value !== "previous_count_out",
                "border-2 border-blue-500": attribute.value === "count_out" && showOutBorder,
              })}
              key={attribute.value}
            >
              <thead className="text-xs font-light">
                <tr className="text-center border-b-2 h-11 sticky top-0 bg-white z-10">
                  {attribute.value === "add_3" && (
                    <>
                      {!showAdd3 && (
                        <td className="text-blue-500 w-thinner">
                          <FontAwesomeIcon icon={faPlus} onClick={handleShowAdd3}></FontAwesomeIcon>
                        </td>
                      )}
                      {showAdd3 && <td>{attribute.label}</td>}
                    </>
                  )}
                  {attribute.value === "add_4" && (
                    <>
                      {showAdd3 && !showAdd4 && (
                        <td className="text-blue-500 w-thinner">
                          <FontAwesomeIcon icon={faPlus} onClick={handleShowAdd4}></FontAwesomeIcon>
                        </td>
                      )}
                      {showAdd4 && <td>{attribute.label}</td>}
                    </>
                  )}
                  {attribute.value === "previous_count_out" && showPrevOuts && (
                    <td>
                      <div className="mb-2 -mt-12 flex">
                        <div className="w-32">
                          <Button
                            className="button button-lg"
                            onClick={setPrevCountOutEqCurrentCountIn}
                            text="Set Prev Out = In"
                          ></Button>
                        </div>
                        <div className="w-32 -mt-0.5">
                          <Button
                            className="button-white button button-lg ml-4"
                            onClick={() => setShowPrevOuts(false)}
                            text="Hide Prev Out"
                          ></Button>
                        </div>
                      </div>
                      {attribute.label}
                    </td>
                  )}
                  {attribute.value !== "add_3" &&
                    attribute.value !== "add_4" &&
                    attribute.value !== "previous_count_out" && <td>{attribute.label}</td>}
                </tr>
              </thead>
              <tbody>
                {salesToAlter.map((sale, i) => {
                  const productToRender = alterSalesObj[sale.product_id] || sale;
                  return (
                    <tr
                      key={productToRender.id}
                      className={cn("border-b text-xs h-11 text-center", {
                        "bg-yellow-50": alterSalesObj[productToRender.product_id],
                        "bg-gray-100": productToRender.productNumber % 2 === 1,
                      })}
                    >
                      {(attribute.value === "price" || attribute.value === "discount_price") && (
                        <td>
                          <CleaveInput
                            name={attribute.value}
                            id={`${attribute.value}_${i}`}
                            numericResult
                            cleaveOptions={{
                              numeral: true,
                              numeralPositiveOnly: true,
                            }}
                            onChange={(e) => handleInputChange(e, productToRender, offlineState)}
                            onClick={(e) => selectCell(e, productToRender)}
                            value={productToRender[attribute.value]}
                            inputClassName="w-20 z-0"
                            prefix={"$"}
                            required={productToRender.isToBeUpdated}
                          />
                        </td>
                      )}
                      {attribute.value === "add_3" && showAdd3 && (
                        <td key={productToRender.id}>
                          <SmallInput
                            name={attribute.value}
                            id={`${attribute.value}_${i}`}
                            onChange={(e) => handleInputChange(e, productToRender, offlineState)}
                            onClick={(e) => selectCell(e, productToRender)}
                            value={productToRender[attribute.value]}
                          />
                        </td>
                      )}
                      {attribute.value === "add_4" && showAdd4 && (
                        <td key={productToRender.id}>
                          <SmallInput
                            name={attribute.value}
                            id={`${attribute.value}_${i}`}
                            onChange={(e) => handleInputChange(e, productToRender, offlineState)}
                            onClick={(e) => selectCell(e, productToRender)}
                            value={productToRender[attribute.value]}
                          />
                        </td>
                      )}
                      {attribute.value === "previous_count_out" && showPrevOuts && (
                        <td key={productToRender.id}>{productToRender.previous_count_out}</td>
                      )}
                      {attribute.value !== "price" &&
                        attribute.value !== "discount_price" &&
                        attribute.value !== "add_3" &&
                        attribute.value !== "add_4" &&
                        attribute.value !== "previous_count_out" && (
                          <td>
                            <SmallInput
                              name={attribute.value}
                              id={`${attribute.value}_${i}`}
                              onChange={(e) => handleInputChange(e, productToRender, offlineState)}
                              onClick={(e) => selectCell(e, productToRender)}
                              value={productToRender[attribute.value]}
                            />
                          </td>
                        )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })}
        <table className="w-1/6">
          <thead className="text-xs font-light sticky top-0 bg-white z-10">
            <tr className="text-left border-b-2 h-11 text-center">
              <td className="w-thinner">SOLD</td>
              <td className="w-thinner">GROSS</td>
              <td className="w-thinner pr-2">DS</td>
            </tr>
          </thead>
          <tbody>
            {salesToAlter.map((saleToRender, i) => {
              const productToRender = alterSalesObj[saleToRender.product_id] || saleToRender;
              return (
                <tr
                  key={productToRender.id}
                  className={cn("border-b text-xs h-11 text-center", {
                    "bg-yellow-50": alterSalesObj[productToRender.product_id],
                    "bg-gray-100": productToRender.productNumber % 2 === 1,
                  })}
                >
                  <td
                    id={`sold${i}`}
                    className={
                      parseInt(productToRender.count_in) +
                        parseInt(productToRender.add) +
                        parseInt(productToRender.add_2) +
                        parseInt(productToRender.add_3) +
                        parseInt(productToRender.add_4) -
                        productToRender.count_out -
                        productToRender.damage_quantity -
                        productToRender.discount_quantity -
                        productToRender.band_comp_quantity -
                        productToRender.ctm_comp -
                        productToRender.management_comp <
                      0
                        ? "bg-red-300"
                        : ""
                    }
                  >
                    {parseInt(productToRender.count_in) +
                      parseInt(productToRender.add) +
                      parseInt(productToRender.add_2) +
                      parseInt(productToRender.add_3) +
                      parseInt(productToRender.add_4) -
                      productToRender.count_out -
                      productToRender.damage_quantity -
                      productToRender.discount_quantity -
                      productToRender.band_comp_quantity -
                      productToRender.ctm_comp -
                      productToRender.management_comp}
                  </td>
                  <td
                    id={`gross${i}`}
                    className={
                      parseInt(productToRender.count_in) +
                        parseInt(productToRender.add) +
                        parseInt(productToRender.add_2) +
                        parseInt(productToRender.add_3) +
                        parseInt(productToRender.add_4) -
                        productToRender.count_out -
                        productToRender.damage_quantity -
                        productToRender.discount_quantity -
                        productToRender.band_comp_quantity -
                        productToRender.ctm_comp -
                        productToRender.management_comp <
                      0
                        ? "bg-red-300 font-semibold text-black"
                        : "font-semibold text-black"
                    }
                  >
                    {(
                      (parseInt(productToRender.count_in) +
                        parseInt(productToRender.add) +
                        parseInt(productToRender.add_2) +
                        parseInt(productToRender.add_3) +
                        parseInt(productToRender.add_4) -
                        productToRender.count_out -
                        productToRender.damage_quantity -
                        productToRender.discount_quantity -
                        productToRender.band_comp_quantity -
                        productToRender.ctm_comp -
                        productToRender.management_comp) *
                      parseFloat(productToRender.price.toString().replace(/,/g, ""))
                    )?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </td>
                  <td className={productToRender.discount_quantity < 0 ? "pr-2 bg-red-300" : "pr-2"}>
                    {(
                      productToRender.discount_quantity *
                      parseFloat(productToRender.discount_price.toString().replace(/,/g, ""))
                    )?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <SalesLayout>
      <EventLayout dataCached={dataCached} setDataCached={setDataCached} afterSubmit={afterSubmit}>
        <div className="flex text-lg float-right -mt-32 -mr-4 text-black">
          <div className="mr-4">
            <div>Gross Sales:</div>
            <div> Discount Sales:</div>
          </div>
          <div>
            <div>
              <div className="float-right">
                {grossAll?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
            </div>
            <div className="float-right">
              {discountAll?.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </div>
          </div>
        </div>
        <div className="mb-4 text-sm">
          <div className={showPrevOuts ? "flex mb-12" : "flex"}>
            <div className="absolute mt-2 ml-2 text-black">
              <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
            </div>
            <div className="w-1/4">
              <Input
                onChange={handleSearchChange}
                value={searchField}
                placeholder="Search for products"
                bigPaddingLeft
              />
            </div>
            <Select
              id="category_filter"
              className="text-xs font-light w-1/4 ml-2"
              placeholder="Filter by category"
              options={categoryOptions}
              onChange={handleFilterCategory}
              value={categoryOptions?.filter((each) => each.value === categoryFilter)}
            />
            {(searchField || categoryFilter) && (
              <button onClick={reset} className="text-blue-500 font-normal italic text-sm ml-2 pl-2">
                Reset
              </button>
            )}
            {!showPrevOuts && (
              <div className="w-32">
                <Button
                  className="button button-lg mx-4"
                  onClick={() => setShowPrevOuts(true)}
                  text="Show Prev Out"
                ></Button>
              </div>
            )}
            {(showButtons || Object.keys(alterSalesObj).length > 0) && (
              <span className="flex justify-centerm mx-4 -mt-0.5">
                <span className="mx-4">
                  <Button className="button button-white" onClick={revert} text="Revert"></Button>
                </span>
                <OnlineWrapper>
                  {!dataCached && <Button className="button" onClick={handleUpdateSales} text="Update"></Button>}
                </OnlineWrapper>
                <OfflineWrapper>
                  <Button className="button" onClick={handleStoreSales} text="Update"></Button>
                </OfflineWrapper>
              </span>
            )}
            {errorMessage?.error && <div className="text-red-500 flex justify-center my-4">{errorMessage.error}</div>}
          </div>
        </div>
        {salesToAlter.length > 0 ? (
          <div className="-mx-12">
            <form className="-mb-8">
              <OnlineWrapper>{renderTable(salesToAlter, false)}</OnlineWrapper>
              <OfflineWrapper>{renderTable(salesToAlter, true)}</OfflineWrapper>
            </form>
          </div>
        ) : (
          <div className="mx-4 my-12">No products have been added for this event yet</div>
        )}
      </EventLayout>
    </SalesLayout>
  );
};

export default EventSales;
